import React, { useState } from "react";
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/utils/cssHelpers";

const rootElementVariants = cva(
  'flex justify-between items-center p-px w-full rounded-sm',
  {
    variants: {
      intent: {
        default: 'bg-mid-neutral-3',
        primary: 'bg-orange',
        secondary: 'bg-purple',
        success: 'bg-success',
        warning: 'bg-warning',
        danger: 'bg-danger',
        info: 'bg-info',
        gradient: 'bg-gradient-bright',
      },
      rounded: {
        true: 'rounded-full',
        false: ''
      },
    elementSize: {
      small: 'text-sm h-9',
      medium: 'text-sm h-12',
      large: 'text-lg h-14',
    },
      focused: {
        true: 'p-px',
        false: ''
      },
    },
    defaultVariants: {
      intent: 'default',
      rounded: false,
      elementSize: 'medium',
      focused: false
    },
  }
)

const innerContainerVariants = cva('flex justify-between items-center gap-2 w-full h-full px-5 bg-white rounded-sm overflow-hidden border border-white', {
  variants: {
    rounded: {
      true: 'rounded-full',
      false: ''
    },
    focused: {
      true: 'border-none m-px h-[calc(100%-2px)]',
      false: ''
    },
    disabled: {
      true: 'bg-light-neutral-3 cursor-not-allowed',
      false: ''
    },
  },
  defaultVariants: {
    rounded: false,
    focused: false,
    disabled: false
  }
})

const inputVariants = cva('w-full outline-none', {
  variants: {
    disabled: {
      true: 'bg-light-neutral-3 cursor-not-allowed',
      false: ''
    }
  },
  defaultVariants: {
    disabled: false
  }
})

export type CaInputProps = React.InputHTMLAttributes<HTMLInputElement> & VariantProps<typeof rootElementVariants> & VariantProps<typeof innerContainerVariants> & {
  ["aria-invalid"]?: boolean;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  rounded?: boolean;
};

function CaInput({
  elementSize,
  intent,
  className,
  rounded,
  disabled,
  onFocus,
  onBlur,
  prefix,
  suffix,
  ...props
}: CaInputProps) {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(event);
  };

  return (
    <div className={cn(rootElementVariants({ elementSize, rounded, intent, focused: isFocused }))}>
      <div
        className={cn(innerContainerVariants({ rounded, focused: isFocused, disabled }))}
      >
        {prefix && <div>{prefix}</div>}
        <input
          type='text'
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={cn(inputVariants({ disabled }))}
          {...props}
        />
        {suffix && <div>{suffix}</div>}
      </div>
    </div>
  );
}

CaInput.defaultProps = {
  elementSize: "medium",
  intent: "default",
  rounded: false,
};

export default CaInput;
